<script setup lang="ts">
  import type { RuleAction, TraceTargetState } from '../../model/rule-trace';
  import { useMatrixFillLabContext } from '../../model/useMatrixFillLab';
  import FieldRef from './FieldRef.vue';
  import LabSection from './LabSection.vue';

  const lab = useMatrixFillLabContext();

  const ACTION_LABEL: Record<RuleAction, string> = { show: 'mostrar', hide: 'ocultar', disable: 'desabilitar' };

  const STATE_CLASS: Record<TraceTargetState, string> = {
    visível: 'bg-green-100 text-green-800',
    oculto: 'bg-[var(--color-gray-09)] text-[var(--color-gray-05)]',
    desabilitado: 'bg-amber-100 text-amber-800',
    ausente: 'bg-red-100 text-red-800',
  };
</script>

<template>
  <LabSection
    title="Trace de regras"
    nested
    open
    :badge="`${lab.ruleTrace.value.filter((entry) => entry.fires).length}/${lab.ruleTrace.value.length} disparadas`"
    hint="Uma regra dispara quando o gatilho tem a opção. Um alvo de 'mostrar' começa oculto; 'ocultar' vence 'mostrar' no mesmo alvo."
    data-testid="lab-rule-trace"
  >
    <p
      v-if="lab.ruleTrace.value.length === 0"
      class="italic text-[var(--color-gray-05)]"
    >
      Nenhuma regra em uso.
    </p>
    <ul
      v-else
      class="flex flex-col gap-1.5"
    >
      <li
        v-for="entry in lab.ruleTrace.value"
        :key="entry.id"
        class="rounded-md border bg-[var(--color-white-01)] p-2"
        :class="entry.fires ? 'border-[var(--theme-secondary)]' : 'border-[var(--color-gray-02)]'"
      >
        <div class="flex items-center gap-2">
          <span
            class="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
            :class="entry.fires ? 'bg-[var(--theme-secondary)] text-white' : 'bg-[var(--color-gray-09)] text-[var(--color-gray-05)]'"
          >
            {{ entry.fires ? 'DISPARADA' : 'em espera' }}
          </span>
          <span class="truncate font-semibold">{{ entry.name }}</span>
        </div>
        <p class="mt-1 flex flex-wrap items-baseline gap-x-1">
          Quando
          <FieldRef :id="entry.triggerFieldId" />
          = "{{ entry.triggerOption }}"
        </p>
        <div
          v-for="(effect, index) in entry.effects"
          :key="index"
          class="mt-1"
        >
          <p class="text-[10px] font-semibold uppercase text-[var(--color-gray-05)]">
            {{ ACTION_LABEL[effect.action] }}
          </p>
          <ul class="flex flex-col gap-0.5">
            <li
              v-for="target in effect.targets"
              :key="target.id"
              class="flex items-center gap-2"
            >
              <FieldRef :id="target.id" />
              <span
                class="ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                :class="STATE_CLASS[target.state]"
              >
                {{ target.state }}
              </span>
            </li>
          </ul>
        </div>
      </li>
    </ul>
  </LabSection>
</template>
